import {Directive, ElementRef, NgZone, OnDestroy} from '@angular/core';

const INJECTED_CLASS = 'apexcharts-radialbar-half-labels';
const HOST_CLASS = 'radialbar-half-labels';

@Directive({selector: 'apx-chart'})
export class RadialbarHalfLabelsDirective implements OnDestroy {
	private observer: MutationObserver;
	private raf: any = null;
	private injecting = false;

	constructor(private el: ElementRef, private ngZone: NgZone) {
		this.ngZone.runOutsideAngular(() => {
			this.observer = new MutationObserver(() => {
				if (this.injecting) {
					return;
				}
				// CC: add the host class as soon as we detect this is a radialBar chart.
				// The class is defined in custom.scss and hides the legend permanently via CSS,
				// so it survives every ApexCharts re-render without any inline style toggling.
				if (!this.el.nativeElement.classList.contains(HOST_CLASS) &&
					this.el.nativeElement.querySelector('.apexcharts-radialbar-hollow')) {
					this.el.nativeElement.classList.add(HOST_CLASS);
				}
				if (this.raf) {
					cancelAnimationFrame(this.raf);
				}
				this.raf = requestAnimationFrame(() => {
					this.raf = null;
					this.inject();
				});
			});
			this.observer.observe(this.el.nativeElement, {childList: true, subtree: true});
		});
	}

	ngOnDestroy() {
		this.observer.disconnect();
		if (this.raf) {
			cancelAnimationFrame(this.raf);
		}
		this.removeInjected();
		this.el.nativeElement.classList.remove(HOST_CLASS);
	}

	private inject() {
		const host: HTMLElement = this.el.nativeElement;

		const hollow = host.querySelector('.apexcharts-radialbar-hollow');
		const graphical = host.querySelector('.apexcharts-graphical');
		if (!hollow || !graphical) {
			return;
		}

		const cx = parseFloat(hollow.getAttribute('cx'));
		const cy = parseFloat(hollow.getAttribute('cy'));
		if (isNaN(cx) || isNaN(cy)) {
			return;
		}

		const trackPaths = host.querySelectorAll('.apexcharts-radialbar-track path');
		if (!trackPaths.length) {
			return;
		}

		// CC: auto-detect matching angle — bars must start at the top (x ≈ cx, y < cy)
		const firstD = (trackPaths[0] as Element).getAttribute('d') || '';
		const firstM = firstD.match(/M\s*([\d.]+)\s+([\d.]+)/);
		if (!firstM || Math.abs(parseFloat(firstM[1]) - cx) > 5 || parseFloat(firstM[2]) >= cy) {
			return;
		}

		// CC: for this angle config bars start at top: (cx, cy-r), so r = cy - y_start
		const radii = [];
		trackPaths.forEach(path => {
			const d = (path as Element).getAttribute('d') || '';
			const match = d.match(/M\s*([\d.]+)\s+([\d.]+)/);
			if (match) {
				radii.push(cy - parseFloat(match[2]));
			}
		});

		// CC: read label text and color from the legend (text color matches series color)
		const legendSeries = host.querySelectorAll('.apexcharts-legend-series');
		if (!legendSeries.length || radii.length !== legendSeries.length) {
			return;
		}

		const items = [];
		legendSeries.forEach(series => {
			const textEl = series.querySelector('.apexcharts-legend-text');
			if (textEl) {
				items.push({
					text: (textEl as HTMLElement).textContent.trim(),
					color: (textEl as HTMLElement).style.color || '#999'
				});
			}
		});

		if (items.length !== radii.length) {
			return;
		}

		this.injecting = true;
		try {
			this.removeInjected();

			const svgNs = 'http://www.w3.org/2000/svg';
			const g = document.createElementNS(svgNs, 'g');
			g.setAttribute('class', INJECTED_CLASS);

			items.forEach((item, i) => {
				const text = document.createElementNS(svgNs, 'text');
				text.setAttribute('x', String(cx - 10));
				text.setAttribute('y', String(cy - radii[i]));
				text.setAttribute('dominant-baseline', 'middle');
				text.setAttribute('text-anchor', 'end');
				text.setAttribute('font-size', '11');
				text.setAttribute('font-family', 'Helvetica, Arial, sans-serif');
				text.setAttribute('fill', item.color);
				text.textContent = item.text;
				g.appendChild(text);
			});

			graphical.appendChild(g);
		}
		finally {
			this.injecting = false;
		}
	}

	private removeInjected() {
		const existing = this.el.nativeElement.querySelector('.' + INJECTED_CLASS);
		if (existing) {
			existing.parentElement?.removeChild(existing);
		}
	}
}
